import { formatDateTime } from "../../../utils/formatters";
import type {
  AlertConfigCreatePayload,
  AlertConfigItem,
  AlertConfigModule,
  AlertRuleConfig,
  DataImportItem,
} from "../../../api/frameworksApi";
import {
  MODULE_CODE_BY_VALUE,
  MODULE_LABEL_MAP,
  alertTypeApiFromMode,
  isStocMode,
  type AddEmailSchedulePayload,
  type SchedulerRow,
} from "./schedulerTypes";

export const ALERT_CONFIG_ORGANIZATION_ID = "DEFAULT";

/** DataTable column key → backend sort field (data-import tab). */
export const SCHEDULER_SORT_COLUMNS: Record<string, string> = {
  moduleName: "moduleName",
  tool: "tool",
  importFrequency: "importFrequency",
  importOn: "importOn",
  time: "time",
  lastTriggeredDateStr: "lastTriggeredDateAndTime",
};

const toTimestamp = (value?: string | null) =>
  value ? new Date(value).getTime() || 0 : 0;

const dayLabel = (day: string) =>
  day ? day.charAt(0).toUpperCase() + day.slice(1).toLowerCase() : day;

const importOnFromSchedule = (item: AlertConfigItem) => {
  if (item.schedule?.frequency === "WEEKLY") {
    const weeklyDays = item.schedule.weeklyDays;
    const days = Array.isArray(weeklyDays)
      ? weeklyDays
      : String(weeklyDays || item.schedule.importOn || "").split(",");
    return days.map((day) => dayLabel(day.trim())).filter(Boolean).join(",");
  }
  return item.schedule?.importOn ?? "";
};

/** Email alert-config API item → table row. */
export const mapEmailAlertToRow = (item: AlertConfigItem, index: number): SchedulerRow => {
  const moduleCode = (item.Module ?? item.module ?? "VM") as AlertConfigModule;
  const createdAt = item.createdDt ?? item.createdDate ?? null;
  const updatedAt = item.updateDt ?? item.updatedDate ?? null;
  const rawId = item.id != null ? Number(item.id) : index + 1;
  return {
    id: Number.isFinite(rawId) ? rawId : index + 1,
    moduleName: MODULE_LABEL_MAP[moduleCode] ?? String(moduleCode),
    moduleCode,
    alertType: item.alertType,
    time: item.schedule?.time ?? "00:00",
    importOn: importOnFromSchedule(item),
    importFrequency: item.schedule?.frequency ?? "",
    status: (item.isActive ?? item.status) !== false,
    tool: "",
    subject: item.subject,
    message: item.message,
    alertConfig: item.alertConfig,
    lastTriggeredDateStr: "",
    createdBy: item.createBy ?? item.createdBy ?? "",
    createdDate: toTimestamp(createdAt),
    updatedBy: item.updateBy ?? item.updatedBy ?? "",
    updatedDate: toTimestamp(updatedAt),
  };
};

/** Data-import API item → table row. */
export const mapDataImportToRow = (item: DataImportItem): SchedulerRow => ({
  id: item.id,
  moduleName: item.moduleName,
  time: item.time,
  importOn: item.importOn,
  importFrequency: item.importFrequency,
  status: item.status,
  tool: item.tool,
  instanceName: item.instanceName,
  environment: item.environment,
  lastTriggeredDateStr: formatDateTime(item.lastTriggeredDateAndTime),
  createdBy: item.createBy ?? "",
  createdDate: item.createdDt ? new Date(item.createdDt).getTime() : 0,
  updatedBy: item.updateBy ?? "",
  updatedDate: item.updateDt ? new Date(item.updateDt).getTime() : 0,
});

/* ── Add/Edit form value helpers ─────────────────────────────────────────── */

export const moduleLabelFromValue = (value: string) =>
  value === "stoc"
    ? "Security Tools Operations Center"
    : value.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

export const toolLabelFromValue = (value: string) => {
  const map: Record<string, string> = {
    aws: "AWS",
    active_directory: "Active Directory",
    linux: "Linux",
    tenable_io: "Tenable IO",
    tenable_security_center: "Tenable Security Center",
  };
  return map[value] ?? value;
};

export const normalizeKey = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

export const toNumberOrNull = (value: number | string | null | undefined) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Build the alert-config create payload from the Add Email Schedule form. */
export const buildAlertConfigPayload = (
  p: AddEmailSchedulePayload,
): AlertConfigCreatePayload => {
  const alertType = alertTypeApiFromMode(p.mode);
  const isReminder = alertType === "TARGET_REMINDER";
  const isExpiry = alertType === "TARGET_EXPIRY";
  const isStoc = isStocMode(p.mode);
  // Reminder and STOC alerts both carry an "alert before" offset.
  const usesOffset = isReminder || isStoc;
  const usesCadence = alertType === "UNMAPPED_ASSET" || alertType === "SLA_BREACH";
  const frequency = usesCadence
    ? ((p.importFrequency.toUpperCase() as "DAILY" | "WEEKLY" | "MONTHLY") || "DAILY")
    : "DAILY";
  const selectedDays = p.importOn.split(",").map((day) => day.trim()).filter(Boolean);
  const apiDays = selectedDays.map((day) => day.toUpperCase());
  const monthlyDate = new Date(p.importOn);
  const monthlyDay =
    usesCadence && frequency === "MONTHLY" && !Number.isNaN(monthlyDate.getTime())
      ? monthlyDate.getDate()
      : null;
  const alertConfig: AlertRuleConfig = {
    startOffsetDays: {
      value: usesOffset ? String(p.alertBefore ?? "") : isExpiry ? "0" : "",
      direction: usesOffset ? "BEFORE" : isExpiry ? "AFTER" : "",
      unit: usesOffset ? p.alertBeforeUnit.toUpperCase() : "",
    },
    repeatDays: isExpiry ? String(p.alertInterval ?? "") : "",
    alertInterval: isExpiry ? String(p.alertInterval ?? "") : "",
    alertFrequency: isExpiry ? String(p.alertFrequency ?? "") : "",
  };
  const schedule = {
    time: usesCadence ? p.importAt : p.scheduleTime,
    frequency,
    interval: 1 as const,
    importOn: usesCadence
      ? frequency === "WEEKLY"
        ? apiDays.join(",")
        : p.importOn || p.importFrequency.toUpperCase()
      : "",
    weeklyDays: usesCadence && frequency === "WEEKLY" ? apiDays : "",
    monthlyDay,
  };
  return {
    Module: MODULE_CODE_BY_VALUE[p.module],
    alertType,
    subject: p.subject,
    message: p.message,
    schedule,
    alertConfig,
    isActive: true,
  };
};
